import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import classNames from 'classnames'
import { ArrowLeft, ArrowRight, Users } from 'lucide-react'
import { useForm, useWatch, type FieldErrors } from 'react-hook-form'
import { toast } from 'react-toastify'
import { handleUploadError } from 'utils/uploadErrorHandler'

import { Button, Modal } from 'components'
import type { TournamentListCardData } from 'components/TournamentListCard'

import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useApp } from 'store/app/hooks'
import { useGame } from 'store/game/hooks'
import { useTournament } from 'store/tournament/hooks'

import abi from '../../../contract/leteCoinABI.json'
import contractAbi from '../../../contract/leteContractABI.json'
import { ethers } from 'ethers'
import { encodeFunctionData } from 'viem'
import { usePrivy, useWallets, getAccessToken } from '@privy-io/react-auth'
import { CoinbaseOnramp } from 'components/CoinbaseOnramp'
import {
  checkUSDCBalance,
  executeUSDCTournamentDeposit,
} from '../../../contract/usdcEscrowMethods'

import { LivePreview, MobilePreviewBar, type PreviewSummary } from './LivePreview'
import { groupStageSummary } from './groupStructure'
import {
  DEFAULT_DRAFT_SETTINGS,
  DEFAULT_GROUP_PLAY,
  DEFAULT_REGION_SETTINGS,
  buildPaypalFormData,
  buildTournamentPayload,
  validateGroupStage,
  validatePrizeSetup,
  type FormationState,
  type GroupPlayUiConfig,
  type PaymentBranch,
} from './payload'
import { StepRail } from './StepRail'
import { BasicsStep } from './steps/BasicsStep'
import { EntryStep } from './steps/EntryStep'
import { FormatStep } from './steps/FormatStep'
import { ReviewStep } from './steps/ReviewStep'
import { ScheduleStep } from './steps/ScheduleStep'
import type {
  Contributor,
  CreateTournamentInput,
  DraftSettings,
  PaymentMethod,
  PrizePosition,
  RegionSettings,
  TeamFormation,
} from './types'
import { StepHeading } from './ui'
import { useObjectUrl } from './useObjectUrl'
import {
  BRACKET_OPTIONS,
  FORMATION_LABELS,
  LAST_STEP,
  PAYMENT_LABELS,
  STEPS,
  formatDate,
  formatPrize,
  stepFields,
  stepOfField,
  teamSizeField,
} from './wizard'

const DEFAULT_POSITIONS: PrizePosition[] = [{ id: '1', prize: '1st Position', percentage: '100' }]
const DEFAULT_VALUES: Partial<CreateTournamentInput> = { payment_method: 'leet_token' }
const BUTTON_FOCUS =
  'focus-visible:ring-2 focus-visible:ring-btn-fancy-primary-end focus-visible:ring-offset-2 focus-visible:ring-offset-light-card dark:focus-visible:ring-offset-dark-card'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export const CreateTournamentModal = () => {
  const { isCreateTournament, newTournament, error, createTournament } =
    useTournament()
  const {
    isCreateTournamentModalOpen,
    setIsCreateTournamentModalOpen,
    setIsLoaderForTournament,
    tournamentCommunityContext,
    setTournamentCommunityContext,
  } = useApp()
  const form = useForm<CreateTournamentInput>({
    defaultValues: DEFAULT_VALUES,
  })
  const { handleSubmit, control, trigger, setValue, getValues, reset } = form
  const { wallets } = useWallets()
  const { sendTransaction } = usePrivy()
  const { games } = useGame()
  // Only fetched once the modal is opened (it is mounted app-wide in Layout).
  const { regionTeams: regionTeamsEnabled } = useFeatureFlags(isCreateTournamentModalOpen)

  const [positions, setPositions] = useState<PrizePosition[]>(DEFAULT_POSITIONS)
  const [contributors, setContributors] = useState<Contributor[]>([])
  const [teamFormation, setTeamFormation] = useState<TeamFormation>('teams')
  const [draftSettings, setDraftSettings] = useState<DraftSettings>(DEFAULT_DRAFT_SETTINGS)
  const [regionSettings, setRegionSettings] = useState<RegionSettings>(DEFAULT_REGION_SETTINGS)
  const [isGroupPlayEnabled, setIsGroupPlayEnabled] = useState<boolean>(false)
  const [groupPlayConfig, setGroupPlayConfig] = useState<GroupPlayUiConfig>(DEFAULT_GROUP_PLAY)
  const [showCoinbaseOnramp, setShowCoinbaseOnramp] = useState<boolean>(false)
  const [coinbaseTournamentData, setCoinbaseTournamentData] = useState<any>(null)
  const [usdcDepositProgress, setUsdcDepositProgress] = useState<string>('')
  const [isProcessingUsdc, setIsProcessingUsdc] = useState<boolean>(false)
  const isSubmittingRef = useRef<boolean>(false)
  // True only between a free/LEET createTournament call and its result, so the
  // success effect can't fire on a stale newTournament from an earlier creation.
  const awaitingCreateRef = useRef<boolean>(false)

  // Wizard state
  const [step, setStep] = useState(0)
  const [maxReached, setMaxReached] = useState(0)
  const [prizeError, setPrizeError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])

  const contractAddress = import.meta.env.VITE_LETE_COIN_CONTRACT_ADDRESS
  const leteContractAddress = import.meta.env.VITE_LETE_CONTRACT_ADDRESS

  const values = useWatch({ control })

  // Once a field has shown an error, re-check it as the organizer fixes it, so
  // messages clear immediately instead of waiting for the next Next click.
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (!name) return
      if (form.getFieldState(name).error) trigger(name)
      if (name === 'startDate' && form.getFieldState('endDate').error) trigger('endDate')
    })
    return () => subscription.unsubscribe()
  }, [form, trigger])
  const selectedPaymentMethod = (values.payment_method || 'leet_token') as PaymentMethod

  // Region teams can only be chosen while the feature flag is on.
  const effectiveFormation: TeamFormation =
    teamFormation === 'region' && !regionTeamsEnabled ? 'teams' : teamFormation
  useEffect(() => {
    if (teamFormation === 'region' && !regionTeamsEnabled) setTeamFormation('teams')
  }, [teamFormation, regionTeamsEnabled])

  const formationFor = (tournamentData: CreateTournamentInput): FormationState => ({
    teamFormation: effectiveFormation,
    selectedFormat: tournamentData.play_format,
    draftSettings,
    regionSettings,
    isGroupPlayEnabled,
    groupPlayConfig,
  })

  const resetWizard = useCallback(() => {
    reset(DEFAULT_VALUES)
    setPositions(DEFAULT_POSITIONS)
    setContributors([])
    setTeamFormation('teams')
    setDraftSettings(DEFAULT_DRAFT_SETTINGS)
    setRegionSettings(DEFAULT_REGION_SETTINGS)
    setIsGroupPlayEnabled(false)
    setGroupPlayConfig(DEFAULT_GROUP_PLAY)
    setPrizeError(null)
    setStep(0)
    setMaxReached(0)
  }, [reset])

  /* ------------------------------------------------------------------------ */
  /* Step navigation                                                          */
  /* ------------------------------------------------------------------------ */

  // When the Review step appeared. The Create button ignores clicks that land
  // right after it (a double-click on "Review" must never create a tournament).
  const reviewShownAtRef = useRef(0)

  const goTo = useCallback((index: number) => {
    if (index === LAST_STEP) reviewShownAtRef.current = Date.now()
    setStep(index)
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: 0 })
      document.getElementById(`ct-step-title-${index}`)?.focus({ preventScroll: true })
    })
  }, [])

  /** Scroll to and focus the first invalid field in a step (after errors render). */
  const focusFirstError = useCallback((index: number) => {
    const run = () => {
      const container = stepRefs.current[index]
      if (!container) return
      const invalid = container.querySelector<HTMLElement>('[aria-invalid="true"], [role="alert"]')
      if (!invalid) return
      const target = (invalid.closest('[data-field]') as HTMLElement) || invalid
      target.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
      const focusable =
        invalid.matches('input, textarea, button')
          ? invalid
          : target.querySelector<HTMLElement>('input:not([type="file"]), textarea, button, [role="radio"][tabindex="0"], [tabindex="0"]')
      focusable?.focus({ preventScroll: true })
    }
    requestAnimationFrame(() => requestAnimationFrame(run))
  }, [])

  const currentPrizeProblem = () =>
    validatePrizeSetup({
      paymentMethod: selectedPaymentMethod,
      prizePool: getValues('prize_pool'),
      positions,
      contributors,
      includeUsdc: true,
    })

  const currentGroupStageProblem = () =>
    validateGroupStage({
      isGroupPlayEnabled,
      groupPlayConfig,
      teamFormation: effectiveFormation,
      numberOfTeams: getValues('number_of_teams'),
      playFormat: getValues('play_format'),
    })

  const validateStep = async (index: number) => {
    const fields = stepFields(index, effectiveFormation)
    const fieldsOk = fields.length ? await trigger(fields) : true
    let prizeOk = true
    if (STEPS[index].id === 'entry') {
      const problem = currentPrizeProblem()
      setPrizeError(problem)
      prizeOk = !problem
    }
    // The group stage error is shown live in the Format step; just block here.
    const groupsOk = STEPS[index].id !== 'format' || !currentGroupStageProblem()
    if (!fieldsOk || !prizeOk || !groupsOk) focusFirstError(index)
    return fieldsOk && prizeOk && groupsOk
  }

  const goNext = async () => {
    if (!(await validateStep(step))) return
    const next = Math.min(step + 1, LAST_STEP)
    setMaxReached((m) => Math.max(m, next))
    goTo(next)
  }

  const onInvalid = (validationErrors: FieldErrors<CreateTournamentInput>) => {
    const fieldNames = Object.keys(validationErrors)
    const target = Math.min(...fieldNames.map(stepOfField), LAST_STEP)
    toast.error('A few details still need fixing. We’ve taken you to them.')
    goTo(target)
    focusFirstError(target)
  }

  // Keep the team size when switching between team signup and solo signup
  // (each mode keeps its own field: max_player_size vs players_per_team).
  const handleFormationChange = (next: TeamFormation) => {
    const fromField = teamSizeField(effectiveFormation)
    const toField = teamSizeField(next)
    if (fromField !== toField) {
      const current = getValues(fromField)
      if (current && !getValues(toField)) setValue(toField, current as never)
    }
    setTeamFormation(next)
  }

  const handlePaymentMethodChange = (method: PaymentMethod) => {
    // Free tournaments always carry a 0 prize pool (was reset({...values, prize_pool: '0'}))
    if (method === 'free') setValue('prize_pool', '0', { shouldValidate: false })
    setPrizeError(null)
  }

  /* ------------------------------------------------------------------------ */
  /* Submit — payment paths (behavior unchanged; payloads via payload.ts)      */
  /* ------------------------------------------------------------------------ */

  const onSubmit = async (
    tournamentData: CreateTournamentInput,
  ): Promise<void> => {
    console.log('🔵 onSubmit called, isSubmitting:', isSubmittingRef.current)

    // Prevent double submission using ref (synchronous check)
    if (isSubmittingRef.current) {
      console.log('⚠️ Submission already in progress, ignoring duplicate')
      return
    }

    console.log('✅ Setting isSubmitting to true')
    isSubmittingRef.current = true

    try {
      // Check if token gate is disabled for testing
      const isTokenGateDisabled = import.meta.env.VITE_DISABLE_TOKEN_GATE === 'true'

      console.log('Token Gate Disabled:', isTokenGateDisabled)
      console.log('Payment Method:', selectedPaymentMethod)
      console.log('positions', positions)

    // Add payment method to tournament data
    tournamentData.payment_method = selectedPaymentMethod

    // Validate payment method specific requirements + prize split (same checks, same order)
    const prizeProblem = validatePrizeSetup({
      paymentMethod: selectedPaymentMethod,
      prizePool: tournamentData.prize_pool,
      positions,
      contributors,
    })
    if (prizeProblem) {
      toast.error(prizeProblem)
      setPrizeError(prizeProblem)
      goTo(2)
      focusFirstError(2)
      isSubmittingRef.current = false
      return
    }

    // Never save a group stage the backend can't run.
    const groupStageProblem = currentGroupStageProblem()
    if (groupStageProblem) {
      toast.error(groupStageProblem)
      goTo(1)
      focusFirstError(1)
      isSubmittingRef.current = false
      return
    }

    const formation = formationFor(tournamentData)
    const buildFor = (branch: PaymentBranch, transactionHash?: string) =>
      buildTournamentPayload({
        branch,
        tournamentData,
        positions,
        contributors,
        communityContext: tournamentCommunityContext,
        formation,
        transactionHash,
      })

    console.log('leteContractAddress', leteContractAddress)
    console.log('contractAddress', contractAddress)

    // Handle different payment methods
    if (selectedPaymentMethod === 'free') {
      // Free tournaments - create immediately without any payment
      console.log('Creating free tournament without payment')

      const tourData = buildFor('free')

      console.log('tourData (free tournament):', tourData)
      awaitingCreateRef.current = true
      createTournament(tourData as any)
      return
    }

    if (selectedPaymentMethod === 'paypal') {
      // PayPal tournaments - INITIATE PAYMENT FIRST, then create tournament after payment succeeds
      console.log('Initiating PayPal payment BEFORE creating tournament')

      const tourData = buildFor('paypal')

      console.log('tourData (PayPal tournament):', tourData)

      // Call the NEW payment initiation endpoint
      const initiatePayPalPayment = async () => {
        try {
          // Get Privy access token
          const privyToken = await getAccessToken()

          if (!privyToken) {
            toast.error('Authentication required. Please log in again.')
            isSubmittingRef.current = false
            return
          }

          const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

          // Convert tourData to FormData for file uploads
          const formData = buildPaypalFormData(tourData)

          console.log('Sending PayPal payment initiation request...')

          const response = await fetch(`${API_BASE_URL}/tournament/payment/initiate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${privyToken}`,
              // Don't set Content-Type - browser will set it with boundary for FormData
            },
            body: formData,
          })

          const data = await response.json()

          if (data.success && data.approvalUrl) {
            console.log('Payment initiated successfully, redirecting to PayPal:', data.approvalUrl)
            toast.success('Redirecting to PayPal to complete payment...')

            // Close modal before redirect
            setIsCreateTournamentModalOpen(false)

            // Redirect to PayPal approval URL
            setTimeout(() => {
              window.location.href = data.approvalUrl
            }, 1000)
          } else {
            toast.error(data.error || 'Failed to initiate PayPal payment. Please try again.')
            console.error('Payment initiation failed:', data)
            isSubmittingRef.current = false
          }
        } catch (error) {
          console.error('Error initiating PayPal payment:', error)
          handleUploadError(error, 'Error connecting to payment service')
          isSubmittingRef.current = false
        }
      }

      initiatePayPalPayment()
      return
    }

    if (selectedPaymentMethod === 'coinbase_usdc') {
      // Coinbase USDC tournaments - Check balance first, then handle deposit
      const prizePoolAmount = parseFloat(tournamentData.prize_pool || '0')
      if (prizePoolAmount <= 0) {
        toast.error('Prize pool must be greater than $0 for Coinbase USDC tournaments')
        isSubmittingRef.current = false
        return
      }

      // Get user's wallet
      const wallet = wallets.find((w) => w.connectorType === 'embedded')
      if (!wallet) {
        toast.error('No wallet found. Please ensure you are logged in.')
        isSubmittingRef.current = false
        return
      }

      const tourData = buildFor('coinbase_usdc')

      try {
        setIsProcessingUsdc(true)
        setUsdcDepositProgress('Switching to Base network...')

        // Use Privy v3's native switchChain for embedded wallets (avoids hidden modal issues)
        try {
          await wallet.switchChain(8453) // Base mainnet
        } catch (switchError: any) {
          console.warn('switchChain failed, falling back to provider method:', switchError)
        }

        setUsdcDepositProgress('Checking USDC balance...')

        // Get wallet provider (now on Base)
        const provider = await wallet.getEthereumProvider()

        // Check user's USDC balance
        const balanceInfo = await checkUSDCBalance(provider, wallet.address, prizePoolAmount)

        console.log('USDC Balance Check:', balanceInfo)

        if (!balanceInfo.hasEnoughBalance) {
          // Insufficient balance - need to use Coinbase onramp
          const deficit = parseFloat(balanceInfo.deficit)

          toast.info(`You need ${deficit.toFixed(2)} more USDC. Opening Coinbase onramp...`)
          setUsdcDepositProgress('')
          setIsProcessingUsdc(false)

          // Store tournament data and show Coinbase Onramp for the FULL amount
          // (User might want to buy the full amount, not just deficit)
          setCoinbaseTournamentData({ ...tourData, needsDeposit: true })
          setShowCoinbaseOnramp(true)
          isSubmittingRef.current = false
          return
        }

        // User has enough balance! Now execute the FULL deposit flow BEFORE creating tournament
        toast.success(`You have ${balanceInfo.currentBalance} USDC. Proceeding with deposit...`)

        // Hoisted above the try: the catch's failed-deposit cleanup deletes this
        // tournament, so it must be reachable there (was a ReferenceError before,
        // which silently skipped the cleanup and left orphan escrow tournaments).
        let createdTournament: any = null

        try {
          // STEP 1: Execute escrow deposit FIRST (approval + deposit)
          setUsdcDepositProgress('Processing USDC deposit...')

          // First, we need a temporary tournament ID to deposit to escrow
          // We'll create the tournament with pending status, deposit, then update
          setUsdcDepositProgress('Creating tournament record...')

          const tempTourData = {
            ...tourData,
            transactionHash: 'PENDING_USDC_DEPOSIT',
            paymentStatus: 'pending',
            escrowStatus: 'pending'
          }

          // Create tournament in database FIRST (to get ID for escrow contract)
          const resultAction = await createTournament(tempTourData as any)

          // Check if tournament creation was successful
          if (!resultAction.payload || ('error' in resultAction.payload && resultAction.payload.error)) {
            throw new Error('Failed to create tournament')
          }

          createdTournament = resultAction.payload as any
          console.log('✅ Tournament created with ID:', createdTournament._id)

          // STEP 2: Now deposit USDC to escrow with the tournament ID
          setUsdcDepositProgress('Depositing USDC to escrow...')

          const depositResult = await executeUSDCTournamentDeposit(
            provider,
            wallet.address,
            createdTournament._id,
            prizePoolAmount,
            (step: string, message: string) => {
              setUsdcDepositProgress(message)
              if (step === 'approve' && message.includes('Requesting')) {
                toast.info('Please approve USDC spending in your wallet...')
              } else if (step === 'deposit' && message.includes('Depositing')) {
                toast.info('Please confirm USDC deposit in your wallet...')
              }
            }
          )

          console.log('✅ USDC deposited successfully:', depositResult.transactionHash)

          // STEP 3: Update tournament with successful deposit info
          setUsdcDepositProgress('Updating tournament status...')

          const privyToken = await getAccessToken()
          const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

          const updateResponse = await fetch(
            `${API_BASE_URL}/tournament/${createdTournament._id}/escrow/transaction`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${privyToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                transactionHash: depositResult.transactionHash,
              }),
            }
          )

          const updateData = await updateResponse.json()

          if (!updateData.success) {
            throw new Error(updateData.error || 'Failed to update tournament')
          }

          console.log('✅ Tournament funded and updated successfully!')
          toast.success(`Tournament funded! TX: ${depositResult.transactionHash.substring(0, 10)}...`)

          // Clean up and close modal
          setIsProcessingUsdc(false)
          setUsdcDepositProgress('')
          setIsCreateTournamentModalOpen(false)
          resetWizard()

        } catch (depositError: any) {
          console.error('❌ Deposit failed:', depositError)

          // Delete the tournament since deposit failed - we don't want fake escrowed tournaments
          try {
            setUsdcDepositProgress('Deposit failed. Removing tournament...')

            const deletePrivyToken = await getAccessToken()
            const DELETE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL

            if (createdTournament?._id) {
              await fetch(`${DELETE_API_BASE_URL}/tournament/${createdTournament._id}`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${deletePrivyToken}`,
                },
              })
              console.log('🗑️ Tournament deleted due to failed deposit')
            }

            toast.error(`USDC deposit failed: ${depositError.message}. Tournament was not created.`)
          } catch (deleteError: any) {
            console.error('❌ Failed to delete tournament after deposit failure:', deleteError)
            toast.error(`Deposit failed and tournament cleanup failed. Please contact support.`)
          }

          setIsProcessingUsdc(false)
          setUsdcDepositProgress('')
          isSubmittingRef.current = false
        }

      } catch (error: any) {
        console.error('Error processing USDC tournament:', error)
        handleUploadError(error, 'Failed to process USDC tournament')
        setIsProcessingUsdc(false)
        setUsdcDepositProgress('')
        isSubmittingRef.current = false
      }
      return
    }

    // If token gate is disabled, skip token transactions for LEET token flow
    if (isTokenGateDisabled && selectedPaymentMethod === 'leet_token') {
      const tourData = buildFor('leet_token_test_mode')

      console.log('tourData (no token gate):', tourData)
      awaitingCreateRef.current = true
      createTournament(tourData as any)
      return
    }

    const depositTokens = async (amount: any) => {
      try {
        const wallet = wallets[0] // Replace this with your desired wallet

        //  First approve the contract to spend the tokens
        const provider = await wallet.getEthereumProvider()
        const amountInString = amount.toString()

        const approveData = encodeFunctionData({
          abi: abi,
          functionName: 'approve',
          args: [
            leteContractAddress,
            ethers.parseEther(amountInString).toString(),
          ],
        })

        const approveRequest = {
          from: wallet.address,
          to: contractAddress,
          data: approveData,
        }

        const approveUiConfig = {
          header: 'Approve Tokens',
          description:
            'Approve the contract to spend your tokens, after this you have to wait for a few seconds to deposit tokens.',
          buttonText: 'Approve',
        }

        const approveTxReceipt = await sendTransaction(
          approveRequest,
          approveUiConfig as any,
        ) as any
        console.log('approveTransactionHash', approveTxReceipt.transactionHash || approveTxReceipt.hash)
        // show etherscan link for transaction hash
        const link = `https://testnet.bscscan.com/tx/${approveTxReceipt.transactionHash || approveTxReceipt.hash}`
        console.log('link', link)

        // Set loader for tournament
        setIsLoaderForTournament(true)

        setTimeout(async () => {
          try {
            const data = encodeFunctionData({
              abi: contractAbi,
              functionName: 'depositTokens',
              args: [ethers.parseEther(amountInString).toString()],
            })

            const transactionRequest = {
              from: wallet.address,
              to: leteContractAddress, // Ensure this is the correct contract address
              data: data,
              // gasLimit: ethers.hexlify('200000'), // Adjust the gas limit as needed
            }

            const depositUiConfig = {
              header: 'Deposit Tokens',
              description: 'Deposit tokens into the tournament.',
              buttonText: 'Deposit',
            }

            setIsLoaderForTournament(false)

            const transactionReceipt = await sendTransaction(
              transactionRequest,
              depositUiConfig as any,
            ) as any

            console.log('transactionReceipt', transactionReceipt)

            if (transactionReceipt) {
              // Create tournament
              const tourData = buildFor(
                'leet_token_deposit',
                transactionReceipt.transactionHash || transactionReceipt.hash,
              )

              console.log('tourData', tourData)

              awaitingCreateRef.current = true
              createTournament(tourData as any)
            }
          } catch (error) {
            console.error('Error during deposit:', error)
          } finally {
            // Stop the loader after the timeout function execution
            setIsLoaderForTournament(false)
          }
        }, 5000)
      } catch (error) {
        console.error('Error during deposit:', error)
        setIsLoaderForTournament(false) // Stop loader in case of error
      }
    }

      depositTokens(tournamentData.prize_pool)

      // createTournament(data)
    } finally {
      isSubmittingRef.current = false
    }
  }

  useEffect(() => {
    if (error) {
      awaitingCreateRef.current = false
      toast(error)
    }
  }, [error])

  // Close modal for non-PayPal tournaments after creation
  // DISABLED FOR USDC: We need to wait for escrow deposit before closing
  useEffect(() => {
    if (
      !isCreateTournament &&
      newTournament &&
      awaitingCreateRef.current &&
      selectedPaymentMethod !== 'paypal' &&
      selectedPaymentMethod !== 'coinbase_usdc'
    ) {
      // Non-PayPal, non-USDC tournament created successfully - close modal
      console.log('Tournament created successfully:', newTournament)
      awaitingCreateRef.current = false
      toast.success('Tournament created')
      setIsCreateTournamentModalOpen(false)
      resetWizard()
    }
  }, [isCreateTournament, newTournament, selectedPaymentMethod, setIsCreateTournamentModalOpen, resetWizard])

  // Handle USDC escrow deposit after tournament creation
  // DISABLED: Now using synchronous flow in onSubmit instead of useEffect.
  // NOTE (pre-existing): handleCoinbaseSuccess below still relies on this effect,
  // so the Coinbase onramp path creates a PENDING_USDC_DEPOSIT tournament and never
  // deposits. Kept as-is; tracked separately.
  useEffect(() => {
    const handleEscrowDeposit = async () => {
      // DISABLED - Using direct synchronous flow now
      return
    }

    handleEscrowDeposit()
  }, [isCreateTournament, newTournament, coinbaseTournamentData])

  // Coinbase Onramp handlers
  const handleCoinbaseSuccess = async (txHash: string) => {
    toast.success('USDC funding successful! Now depositing to escrow...')

    if (coinbaseTournamentData) {
      try {
        // Get user's wallet
        const wallet = wallets.find((w) => w.connectorType === 'embedded')
        if (!wallet) {
          toast.error('No wallet found')
          setShowCoinbaseOnramp(false)
          return
        }

        // Switch to Base network using Privy's native method
        try {
          await wallet.switchChain(8453)
        } catch (switchError: any) {
          console.warn('switchChain failed in Coinbase onramp handler:', switchError)
        }

        const provider = await wallet.getEthereumProvider()
        const prizePoolAmount = parseFloat(coinbaseTournamentData.prize_pool || '0')

        // Store data for escrow deposit after tournament creation
        const tourDataWithDepositInfo = {
          ...coinbaseTournamentData,
          transactionHash: 'PENDING_USDC_DEPOSIT',
          needsDeposit: true,
          wallet,
          provider,
          prizePoolAmount,
        }

        setCoinbaseTournamentData(tourDataWithDepositInfo)
        setShowCoinbaseOnramp(false)

        // Create tournament - the useEffect above will handle escrow deposit
        createTournament(tourDataWithDepositInfo)

      } catch (error: any) {
        console.error('Error after Coinbase onramp:', error)
        handleUploadError(error, 'Failed to process transaction')
        setShowCoinbaseOnramp(false)
      }
    }
  }

  const handleCoinbaseError = (error: string) => {
    toast.error(error)
    setShowCoinbaseOnramp(false)
    setCoinbaseTournamentData(null)
    isSubmittingRef.current = false
  }

  const handleCoinbaseClose = () => {
    setShowCoinbaseOnramp(false)
    setCoinbaseTournamentData(null)
    toast.info('Coinbase funding cancelled')
    isSubmittingRef.current = false
  }

  /* ------------------------------------------------------------------------ */
  /* Derived view data: completeness, preview card, summary                   */
  /* ------------------------------------------------------------------------ */

  const solo = effectiveFormation !== 'teams'
  const teamSizeValue = values[teamSizeField(effectiveFormation)] as string | number | undefined
  const teamSizeNum = parseInt(String(teamSizeValue ?? '')) || 0

  const complete = useMemo(() => {
    const basics = !!values.game && (values.tournament_name?.trim().length ?? 0) >= 3
    const format =
      !!values.play_format &&
      teamSizeNum >= 1 &&
      teamSizeNum <= 50 &&
      (solo || !!values.number_of_teams) &&
      !validateGroupStage({
        isGroupPlayEnabled,
        groupPlayConfig,
        teamFormation: effectiveFormation,
        numberOfTeams: values.number_of_teams,
        playFormat: values.play_format,
      })
    const entry =
      validatePrizeSetup({
        paymentMethod: selectedPaymentMethod,
        prizePool: values.prize_pool,
        positions,
        contributors,
        includeUsdc: true,
      }) === null && (selectedPaymentMethod === 'free' || !!values.prize_pool)
    const start = values.startDate ? new Date(values.startDate) : null
    const end = values.endDate ? new Date(values.endDate) : null
    const schedule = !!start && !!end && end.getTime() >= start.getTime()
    return [basics, format, entry, schedule, basics && format && entry && schedule]
  }, [values, teamSizeNum, solo, selectedPaymentMethod, positions, contributors, isGroupPlayEnabled, groupPlayConfig, effectiveFormation])

  const coverUrl = useObjectUrl(values.cover_photo as Maybe<File>)
  const selectedGame = useMemo(() => games?.find((g: any) => g._id === values.game), [games, values.game])

  const previewCard: TournamentListCardData = {
    tournament_name: values.tournament_name?.trim() || 'Your tournament',
    prize_pool: selectedPaymentMethod === 'free' ? 0 : parseFloat(values.prize_pool || '') || 0,
    number_of_teams: solo ? 0 : parseInt(values.number_of_teams || '') || 0,
    bracket: values.play_format || '',
    game: selectedGame,
    winner: undefined,
    communityId: tournamentCommunityContext
      ? { _id: tournamentCommunityContext.communityId, community_name: tournamentCommunityContext.communityName }
      : undefined,
    cover_photo: coverUrl ? { url: coverUrl } : undefined,
    teams: [],
    date: { start: values.startDate || new Date().toDateString() },
  }

  const bracketShort = BRACKET_OPTIONS.find((b) => b.id === values.play_format)?.label
  const previewSummary: PreviewSummary = [
    { label: 'Format', value: [FORMATION_LABELS[effectiveFormation], bracketShort].filter(Boolean).join(' · ') },
    { label: 'Team size', value: teamSizeNum ? `${teamSizeNum}v${teamSizeNum}` : '—', muted: !teamSizeNum },
    { label: 'Capacity', value: solo ? 'Unlimited solo signups' : values.number_of_teams ? `${values.number_of_teams} teams` : '—', muted: !solo && !values.number_of_teams },
    ...(isGroupPlayEnabled
      ? [
          {
            label: 'Groups',
            value: groupStageSummary({
              config: groupPlayConfig,
              teamFormation: effectiveFormation,
              numberOfTeams: values.number_of_teams,
              playFormat: values.play_format,
            }).short,
          },
        ]
      : []),
    { label: 'Funding', value: PAYMENT_LABELS[selectedPaymentMethod] },
    { label: 'Prize pool', value: formatPrize(values.prize_pool, selectedPaymentMethod) },
    { label: 'Starts', value: formatDate(values.startDate), muted: !values.startDate },
  ]

  const isBusy = isCreateTournament || isProcessingUsdc
  const isReview = step === LAST_STEP

  const handleCreateClick = () => {
    if (!isReview || isBusy) return
    if (Date.now() - reviewShownAtRef.current < 600) return
    if (isSubmittingRef.current) {
      console.log('⚠️ Create blocked - already submitting')
      return
    }
    handleSubmit(onSubmit, onInvalid)()
  }

  return (
    <>
      {showCoinbaseOnramp && coinbaseTournamentData && (
        <CoinbaseOnramp
          key={Date.now()} // Force new component instance each time
          amount={coinbaseTournamentData.prize_pool}
          onSuccess={handleCoinbaseSuccess}
          onError={handleCoinbaseError}
          onClose={handleCoinbaseClose}
        />
      )}

    <Modal
      title="Create a tournament"
      isOpened={isCreateTournamentModalOpen}
      withCard={false}
      size="wide"
      onClose={() => {
        setIsCreateTournamentModalOpen(false)
        setTournamentCommunityContext(null) // Clear community context when modal closes
        isSubmittingRef.current = false // Reset submission state when modal closes
      }}
    >
      <form
        noValidate
        className="flex h-[85vh] w-full overflow-hidden rounded-2xl border border-light-border-primary dark:border-dark-border-primary bg-light-card dark:bg-dark-card font-primary md:h-[88vh]"
        onSubmit={(e) => {
          e.preventDefault()
          // Enter means "Next" on earlier steps. A tournament is only ever created
          // by an explicit click on "Create tournament" — never by a form submit.
          if (!isReview) goNext()
        }}
      >
        {/* Form column */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="border-b border-light-border-primary dark:border-dark-border-primary">
            {tournamentCommunityContext && (
              <div className="flex items-center gap-2 px-4 pt-3 text-xs sm:px-6">
                <Users size={14} className="text-purple" aria-hidden />
                <span className="text-light-typo-gray dark:text-dark-typo-gray">Community tournament for</span>
                <span className="truncate font-semibold text-light-typo-primary dark:text-dark-typo-primary">
                  {tournamentCommunityContext.communityName}
                </span>
              </div>
            )}
            <StepRail
              current={step}
              maxReached={maxReached}
              complete={complete}
              onSelect={(i) => {
                if (i <= maxReached) goTo(i)
              }}
            />
          </div>

          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-6 sm:px-6">
            {STEPS.map((s, index) => (
              <div
                key={s.id}
                ref={(el) => {
                  stepRefs.current[index] = el
                }}
                hidden={index !== step}
                role="group"
                aria-labelledby={`ct-step-title-${index}`}
                className={classNames('mx-auto max-w-2xl', index === step && 'motion-safe:animate-step-in')}
              >
                <StepHeading id={`ct-step-title-${index}`} title={s.title} intro={s.intro} />
                {s.id === 'basics' && <BasicsStep form={form} />}
                {s.id === 'format' && (
                  <FormatStep
                    form={form}
                    regionTeamsEnabled={regionTeamsEnabled}
                    teamFormation={effectiveFormation}
                    onTeamFormationChange={handleFormationChange}
                    draftSettings={draftSettings}
                    setDraftSettings={setDraftSettings}
                    regionSettings={regionSettings}
                    setRegionSettings={setRegionSettings}
                    isGroupPlayEnabled={isGroupPlayEnabled}
                    setIsGroupPlayEnabled={setIsGroupPlayEnabled}
                    groupPlayConfig={groupPlayConfig}
                    setGroupPlayConfig={setGroupPlayConfig}
                  />
                )}
                {s.id === 'entry' && (
                  <EntryStep
                    form={form}
                    onPaymentMethodChange={handlePaymentMethodChange}
                    positions={positions}
                    setPositions={(next) => {
                      setPositions(next)
                      if (prizeError) setPrizeError(null)
                    }}
                    contributors={contributors}
                    setContributors={(next) => {
                      setContributors(next)
                      if (prizeError) setPrizeError(null)
                    }}
                    prizeError={prizeError}
                  />
                )}
                {s.id === 'schedule' && <ScheduleStep form={form} />}
                {s.id === 'review' && (
                  <ReviewStep
                    onEdit={goTo}
                    incompleteSteps={complete.slice(0, -1).flatMap((ok, i) => (ok ? [] : [i]))}
                    progress={isProcessingUsdc ? usdcDepositProgress : undefined}
                    data={{
                      gameName: selectedGame?.name,
                      name: values.tournament_name?.trim(),
                      description: values.tournament_description?.trim(),
                      hasBanner: values.cover_photo instanceof File,
                      hasLogo: values.avatar instanceof File,
                      teamFormation: effectiveFormation,
                      playFormat: values.play_format,
                      numberOfTeams: values.number_of_teams,
                      teamSize: teamSizeValue,
                      draftSettings,
                      regionSettings,
                      isGroupPlayEnabled,
                      groupPlayConfig,
                      paymentMethod: selectedPaymentMethod,
                      prizePool: values.prize_pool,
                      positions,
                      contributors,
                      startDate: values.startDate,
                      endDate: values.endDate,
                    }}
                  />
                )}
              </div>
            ))}
          </div>

          <MobilePreviewBar card={previewCard} summary={previewSummary} complete={complete} />

          <div className="flex items-center justify-between gap-3 border-t border-light-border-primary dark:border-dark-border-primary px-4 py-3 sm:px-6">
            <button
              type="button"
              onClick={() => goTo(Math.max(0, step - 1))}
              disabled={step === 0 || isBusy}
              className={classNames(
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary transition-colors hover:bg-light-gray-light dark:hover:bg-dark-bg-secondary disabled:invisible',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-btn-fancy-primary-end',
              )}
            >
              <ArrowLeft size={16} aria-hidden /> Back
            </button>
            <span className="[display:none] text-xs tabular-nums text-light-typo-gray dark:text-dark-typo-gray sm:inline">
              Step {step + 1} of {STEPS.length}
            </span>
            {isReview ? (
              <Button
                key="create"
                size="md"
                isFancy
                transform="none"
                buttonType="button"
                onClick={handleCreateClick}
                loading={isBusy}
                disabled={isBusy}
                className={classNames('min-w-[11rem]', BUTTON_FOCUS)}
              >
                {isProcessingUsdc ? 'Processing USDC…' : isCreateTournament ? 'Creating…' : 'Create tournament'}
              </Button>
            ) : (
              <Button
                key="next"
                size="md"
                isFancy
                transform="none"
                buttonType="button"
                onClick={goNext}
                endIcon={<ArrowRight size={16} aria-hidden className="ml-1.5 text-black" />}
                className={classNames('min-w-[8rem]', BUTTON_FOCUS)}
              >
                {step === LAST_STEP - 1 ? 'Review' : 'Next'}
              </Button>
            )}
          </div>
        </div>

        {/* Live preview column (desktop) */}
        <aside
          aria-label="Live preview"
          className="[display:none] w-[340px] flex-shrink-0 overflow-y-auto border-l border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary/60 dark:bg-dark-bg/50 p-5 lg:block"
        >
          <LivePreview card={previewCard} summary={previewSummary} complete={complete} />
        </aside>
      </form>
    </Modal>
    </>
  )
}
